/**
 * Real-time service for WebSocket-based updates
 * Replaces polling service with immediate real-time updates
 * Includes fallback to polling when WebSocket fails
 */

import pollingService from '../api/pollingService';

class RealTimeService {
  constructor() {
    this.websocket = null;
    this.subscriptions = new Map(); // eventType -> Set of handlers
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isConnected = false;
    this.connectionStatus = 'disconnected';
    this.eventListeners = new Set();
    this.apiClient = null;
  }

  /**
   * Initialize the real-time service with an API client
   * @param {Object} apiClient - The API client instance
   */
  initialize(apiClient) {
    this.apiClient = apiClient;
    console.log('🔌 [RealTimeService] Initialized with API client');
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    if (!this.authToken) return;
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      console.log('🔌 [RealTimeService] WebSocket already connected');
      return;
    }

    const wsUrl = process.env.REACT_APP_WEBSOCKET_URL || 'wss://sabalsql-dbam-ji2csljmda-uk.a.run.app/api/ws';
    console.log('🔌 [RealTimeService] Connecting to WebSocket:', wsUrl);
    
    this.websocket = new WebSocket(wsUrl);
    this.connectionStatus = 'connecting';
    this.notifyStatusChange();
    
    this.websocket.onopen = () => {
      this.sendMessage({ action: 'authenticate', token: this.authToken });
      console.log('🔌 [RealTimeService] WebSocket connected');
      this.isConnected = true;
      this.connectionStatus = 'connected';
      this.reconnectAttempts = 0;
      this.notifyStatusChange();
      
      // Resubscribe to all events after reconnection
      this.resubscribeAll();
      
      // Send a general subscription message for telemetry updates
      console.log('🔌 [RealTimeService] Sending initial subscription for telemetry_update');
      // Use string key to avoid eslint camelcase warnings (server expects snake_case).
      this.sendMessage({ action: 'subscribe', 'event_type': 'telemetry_update' });
    };

    this.websocket.onmessage = (event) => {
      console.log('🔌 [RealTimeService] Raw WebSocket message received:', event.data);
      this.handleMessage(event);
    };

    this.websocket.onclose = (event) => {
      console.log('🔌 [RealTimeService] WebSocket disconnected:', event.code, event.reason);
      this.isConnected = false;
      this.connectionStatus = 'disconnected';
      this.notifyStatusChange();
      this.handleReconnect();
    };

    this.websocket.onerror = (error) => {
      console.error('🔌 [RealTimeService] WebSocket error:', error);
      this.connectionStatus = 'error';
      this.notifyStatusChange();
    };
  }

  /**
   * Subscribe to real-time events
   * @param {string} eventType - Type of event to subscribe to
   * @param {function} handler - Function to call when event is received
   */
  subscribe(eventType, handler) {
    if (!this.subscriptions.has(eventType)) {
      this.subscriptions.set(eventType, new Set());
    }
    this.subscriptions.get(eventType).add(handler);
    
    // Send subscription message to server
    if (this.isConnected) {
      this.sendMessage({ action: 'subscribe', 'event_type': eventType });
    }
    
    const totalHandlers = this.subscriptions.get(eventType).size;
    console.log(`🔌 [RealTimeService] Subscribed to ${eventType} (${totalHandlers} handlers total)`);
  }

  /**
   * Unsubscribe from real-time events
   * @param {string} eventType - Type of event to unsubscribe from
   * @param {function} handler - Specific handler to remove
   */
  unsubscribe(eventType, handler) {
    if (this.subscriptions.has(eventType)) {
      const hadHandler = this.subscriptions.get(eventType).has(handler);
      this.subscriptions.get(eventType).delete(handler);
      
      const remainingHandlers = this.subscriptions.get(eventType).size;
      console.log(`🔌 [RealTimeService] Unsubscribed from ${eventType} (had handler: ${hadHandler}, ${remainingHandlers} handlers remaining)`);
      
      // If no more handlers for this event type, send unsubscribe
      if (remainingHandlers === 0) {
        if (this.isConnected) {
          this.sendMessage({ action: 'unsubscribe', 'event_type': eventType });
        }
        console.log(`🔌 [RealTimeService] No more handlers for ${eventType}, sent unsubscribe to server`);
      }
    } else {
      console.log(`🔌 [RealTimeService] Attempted to unsubscribe from ${eventType} but no subscriptions exist`);
    }
  }

  /**
   * Handle incoming WebSocket messages
   * @param {MessageEvent} event - WebSocket message event
   */
  handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      const { type, data, timestamp } = message;
      
      // Server sends connection_confirmed on connect — acknowledge silently.
      if (type === 'connection_confirmed') {
        console.log('🔌 [RealTimeService] Connection confirmed, client:', message.client_id || 'unknown');
        return;
      }
      
      console.log('🔌 [RealTimeService] Received message:', type, data);
      
              if (this.subscriptions.has(type)) {
        // For telemetry_update events, extract the nested data structure
        let processedData = data;
        if (type === 'telemetry_update' && data.data) {
          // Extract the actual telemetry record from the nested structure
          processedData = data.data;
          console.log('🔌 [RealTimeService] Extracted telemetry data:', processedData);
        }
        
        const handlers = this.subscriptions.get(type);
        console.log(`🔌 [RealTimeService] Found ${handlers.size} handlers for event type: ${type}`);
        
        handlers.forEach((handler, index) => {
          try {
            console.log(`🔌 [RealTimeService] Calling handler ${index + 1}/${handlers.size} for ${type}`);
            handler(processedData, timestamp);
          } catch (error) {
            console.error('Error in real-time event handler:', error);
          }
        });
      } else {
        console.log(`🔌 [RealTimeService] No handlers registered for event type: ${type}`);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  /**
   * Send message to WebSocket server
   * @param {Object} message - Message to send
   */
  sendMessage(message) {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      console.log('🔌 [RealTimeService] Sending message to server:', message);
      this.websocket.send(JSON.stringify(message));
    } else {
      console.warn('🔌 [RealTimeService] Cannot send message, WebSocket not open. ReadyState:', this.websocket?.readyState);
    }
  }

  /**
   * Handle reconnection attempts
   */
  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`🔌 [RealTimeService] Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
      
      setTimeout(() => {
        this.connect();
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('🔌 [RealTimeService] Max reconnection attempts reached, falling back to polling');
      this.fallbackToPolling();
    }
  }

  /**
   * Fallback to polling service when WebSocket fails
   */
  fallbackToPolling() {
    console.log('🔄 [RealTimeService] Falling back to polling service');
    this.connectionStatus = 'fallback';
    this.notifyStatusChange();
    
    // Initialize polling service with API client
    if (this.apiClient) {
      pollingService.initialize(this.apiClient);
    }
  }

  /**
   * Resubscribe to all events after reconnection
   */
  resubscribeAll() {
    console.log('🔌 [RealTimeService] Resubscribing to all events');
    this.subscriptions.forEach((handlers, eventType) => {
      this.sendMessage({ action: 'subscribe', 'event_type': eventType });
    });
  }

  /**
   * Add status change listener
   * @param {function} listener - Function to call when status changes
   */
  addStatusListener(listener) {
    this.eventListeners.add(listener);
  }

  /**
   * Remove status change listener
   * @param {function} listener - Function to remove
   */
  removeStatusListener(listener) {
    this.eventListeners.delete(listener);
  }

  /**
   * Notify all status listeners of status change
   */
  notifyStatusChange() {
    this.eventListeners.forEach(listener => {
      try {
        listener(this.getConnectionStatus());
      } catch (error) {
        console.error('Error in status listener:', error);
      }
    });
  }

  /**
   * Get current connection status
   * @returns {Object} Connection status object
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      connectionStatus: this.connectionStatus,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    };
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    if (this.websocket) {
      this.websocket.close();
    }
  }

  /**
   * Get current subscriptions
   * @returns {Map} Current subscriptions
   */
  getSubscriptions() {
    return new Map(this.subscriptions);
  }
}

// Create singleton instance
export const realtimeService = new RealTimeService();
export default realtimeService;
